import React, { useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { UserContext } from 'context';
import i18n from 'i18n-js';
import Grid from '@material-ui/core/Grid';

import { userGroupsActions } from 'state/modules/userGroups';
import { TextField } from 'components/FormFields';

import { BaseModal } from 'components/UI';
import { MultipleVehicleGroupsSelect } from 'components/AsyncFields';
import { UserGroupsTypesSelect, UserGroupsRolesSelect } from 'components/DataFields';

const UpdateUserGroupModal = ({ id, userGroup, errors, open, handleClick }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const { orgType } = useContext(UserContext);
  const vg_params = {
    group_type: ['CUSTOMER', 'OWNER'],
    limit: 20,
  };

  const formSchema = Yup.object().shape({
    name: Yup.string().required(i18n.t('common.required_field')),
    default_vehicle_group_id: Yup.array(Yup.object().shape({ id: Yup.number() })),
    role_default: Yup.string().required(i18n.t('common.required_field')),
    user_group_type: Yup.string().required(i18n.t('common.required_field')),
  });

  const { loading, updateUserGroupSuccess } = useSelector((state) => state.userGroups);

  const initialValues = {
    name: userGroup?.name || '',
    default_vehicle_group_id: userGroup?.default_vehicle_group || [],
    role_default: userGroup?.role_default || '',
    user_group_type: userGroup?.user_group_type || '',
  };

  const onSubmit = (values) => {
    let payload = {
      ...values,
      default_vehicle_group_id: values.default_vehicle_group_id.map((vg) => vg.id),
      id,
    };

    // remove empty field
    if (!payload.default_vehicle_group_id) delete payload.default_vehicle_group_id;

    dispatch(userGroupsActions.fetchCreateUserGroup(payload, 'details', true));
  };

  useEffect(() => {
    if (updateUserGroupSuccess) {
      if (id) {
        dispatch(userGroupsActions.fetchUserGroupById(id));
        dispatch(userGroupsActions.resetUpdateUserGroup());
        handleClick();
      }
    }
  }, [id, updateUserGroupSuccess, handleClick, dispatch]);

  const handleSubmit = () => {
    formRef.current.submitForm();
  };

  const getOptionLabel = (option) => {
    const groupTypeName =
      option?.group_type === 'OWNER'
        ? i18n.t('user_groups.owner_vehicle_group')
        : i18n.t('user_groups.vehicle_group');
    return groupTypeName + ' - ' + option?.name;
  };

  return (
    <BaseModal
      size="sm"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={handleSubmit}
      onCancel={handleClick}
      closeButtonType="icon"
      enableCloseButton={false}
      loading={loading}
      title={i18n.t('user_groups.update_user_group')}
      enterButtonLabel={i18n.t('common.update')}
      privileges={{ action: 'update', subject: 'user_group' }}
      enterButtonProps={{ 'data-cy': 'save-user-group' }}
    >
      <Grid container direction="row" justifyContent="center" alignItems="center">
        <Grid item xs={11}>
          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            validationSchema={formSchema}
            enableReinitialize={true}
            onSubmit={onSubmit}
          >
            {({ handleSubmit }) => (
              <form noValidate onSubmit={handleSubmit}>
                <Grid
                  container
                  spacing={2}
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                >
                  <Grid item xs={12}>
                    <Field
                      type="text"
                      name="name"
                      component={TextField}
                      asyncErrors={errors?.name}
                      label={i18n.t('common.name')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      name="default_vehicle_group_id"
                      component={MultipleVehicleGroupsSelect}
                      customParams={vg_params}
                      asyncErrors={errors?.default_vehicle_group_id}
                      defaultVehicleGroup={userGroup?.default_vehicle_group}
                      label={i18n.t(
                        orgType === 'msOwnerOrg' ? 'fleets.fleets' : 'common.vehicle_group'
                      )}
                      getOptionLabel={getOptionLabel}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      name="user_group_type"
                      component={UserGroupsTypesSelect}
                      asyncErrors={errors?.user_group_type}
                      label={i18n.t('user_groups.user_group_type')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      name="role_default"
                      component={UserGroupsRolesSelect}
                      asyncErrors={errors?.role_default}
                      label={i18n.t('user_groups.default_role')}
                    />
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Grid>
      </Grid>
    </BaseModal>
  );
};

UpdateUserGroupModal.propTypes = {
  id: PropTypes.number.isRequired,
  errors: PropTypes.object,
  open: PropTypes.bool.isRequired,
  userGroup: PropTypes.any,
  handleClick: PropTypes.func.isRequired,
};

UpdateUserGroupModal.defaultProps = {
  open: false,
  userGroup: null,
  handleClick: () => {},
};

export default UpdateUserGroupModal;
